import { useState } from 'react'
import { repository } from '../../data'
import { Modal } from '../kit'
import type { View } from './derive'
import { useArm, useWrite } from './parts'
import { PeoplePicker } from './Pickers'

/** Make a team (teamId null), or rename one, change who is in it, its levels, or delete it. */
export function TeamPanel({ view, teamId, onClose }: { view: View; teamId: string | null; onClose: () => void }): JSX.Element {
  const team = teamId ? view.team(teamId) : undefined
  const [name, setName] = useState(team?.name ?? '')
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set())
  const arm = useArm()
  const create = useWrite(() => repository.createTeam(name, [...chosen]).then((r) => (onClose(), r)), () => `Made the team ${name.trim()}`)
  const rename = useWrite(() => repository.renameTeam(teamId as string, name), () => `Renamed to ${name.trim()}`)
  const member = useWrite(
    (userId: string, on: boolean) => repository.setTeamMember(teamId as string, userId, on),
    (userId, on) => `${view.person(userId)?.name} ${on ? 'joined' : 'left'} ${team?.name}${on ? '' : ' · their sessions through it closed'}`,
  )
  const level = useWrite(
    (target: { groupId: string | null; machineId: string | null }, l: 'full' | 'restricted' | null) => repository.setRule({ teamId, ...target, level: l }),
    (target, l) => {
      const name = target.groupId ? view.group(target.groupId)?.name : view.machine(target.machineId ?? '')?.name
      return l ? `${team?.name} → ${name} is now ${l}` : `${team?.name} can no longer reach ${name}`
    },
  )
  const remove = useWrite(() => repository.deleteTeam(teamId as string).then((r) => (onClose(), r)), () => `Deleted ${team?.name}; its access went with it`)

  if (!team) {
    return (
      <Modal
        title="New team"
        subtitle="People in a team get whatever the team is let into. A person can be in several teams."
        onClose={onClose}
        footer={<button className="primary" disabled={!name.trim() || create.pending} onClick={() => create.run()}>Make the team</button>}
      >
        <div className="stack">
          <label className="stack" style={{ gap: 4 }}>
            <span className="field-label">Name</span>
            <input value={name} placeholder="Field · Site A" onChange={(e) => setName(e.currentTarget.value)} data-autofocus />
          </label>
          <span className="field-label">Who is in it</span>
          <PeoplePicker people={view.people} chosen={chosen} onChange={setChosen} />
        </div>
      </Modal>
    )
  }

  const rules = view.page.world.rules.filter((r) => r.teamId === team.id)
  const outside = view.people.filter((p) => !team.memberIds.includes(p.id))
  return (
    <Modal title={team.name} subtitle={`${team.memberIds.length} people · ${rules.length} groups`} onClose={onClose}>
      <div className="stack cx-panel-body">
        <div className="row" style={{ gap: 8 }}>
          <input value={name} onChange={(e) => setName(e.currentTarget.value)} aria-label="Team name" style={{ flex: 1 }} />
          <button disabled={name.trim() === team.name || !name.trim() || rename.pending} onClick={() => rename.run()}>Rename</button>
        </div>

        <span className="field-label">People</span>
        <ul className="cx-list">
          {team.memberIds.map((id) => (
            <li key={id} className="cx-list__row">
              <span>{view.person(id)?.name ?? 'Deactivated account'}</span>
              <button className={arm.armed(`m${id}`) ? 'danger is-armed' : 'danger'} onClick={() => arm.press(`m${id}`, () => member.run(id, false))}>
                {arm.armed(`m${id}`) ? 'Sure?' : 'Take out'}
              </button>
            </li>
          ))}
        </ul>
        <select value="" aria-label="Add a person" onChange={(e) => e.currentTarget.value && member.run(e.currentTarget.value, true)}>
          <option value="">+ Add a person…</option>
          {outside.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        <span className="field-label">Let into</span>
        {rules.length === 0 ? <span className="muted">Nothing yet — switch Access to Teams and press a square.</span> : null}
        <ul className="cx-list">
          {rules.map((r) => (
            <li key={r.id} className="cx-list__row">
              <span>{r.groupId ? view.group(r.groupId)?.name : view.machine(r.machineId ?? '')?.name}{r.expiresAt ? <span className="muted cx-small"> · until {new Date(r.expiresAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</span> : null}</span>
              <div className="segmented" role="tablist" aria-label="Level">
                {(['full', 'restricted'] as const).map((l) => (
                  <button key={l} role="tab" aria-selected={r.level === l} onClick={() => r.level !== l && level.run({ groupId: r.groupId, machineId: r.machineId }, l)}>
                    {l === 'full' ? 'Full' : 'Restricted'}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
        <span className="muted cx-small">Restricted logs in as a separate account with no docker and no sudo. Machines without one let nobody in at that level.</span>

        <button className={arm.armed('del') ? 'danger is-armed' : 'danger'} onClick={() => arm.press('del', () => remove.run())}>
          {arm.armed('del') ? 'Press again: delete the team and its access' : 'Delete team'}
        </button>
      </div>
    </Modal>
  )
}
